import ContentWrapper from '../../../common/ContentWrapper';
import type { SquareFeature as SquareFeatureType } from '../../../../../types/MiniSiteContents';
import Carousel from '../../../common/Carousel';
import ImageWithFallback from '../../../common/ImageWithFallback';
import isAbsoluteUrl from '../../../../core/helpers/isAbsoluteUrl';
import useRelativeUrl from '../../../../core/hooks/useRelativeUrl';
import Markdown from '../../../common/Markdown/Markdown';

type Props = {
  miniSiteContent: SquareFeatureType;
};

const MiniSiteSquareFeature = ({ miniSiteContent }: Props) => {
  const { contentTitle, contents } = miniSiteContent;
  const { getRelativeUrl } = useRelativeUrl();
  const settings = {
    className: '-mx-4', // remove spaces at the far left/right ends from slider container
    cardSpacing: 'px-4', // add spaces at both of the sides of cards
    infinite: false,
    slidesToShow: 3,
    draggable: false,
    prevBtnStyle: '-left-3 top-33 card-carousel-arrow-btn',
    nextBtnStyle: '-right-3 top-33 card-carousel-arrow-btn',
    prevArrowStyle: 'stroke-2',
    nextArrowStyle: 'stroke-2',
    responsive: [
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2.5,
          arrows: false,
          draggable: true,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1.2,
          arrows: false,
          draggable: true,
        },
      },
    ],
  };

  return (
    <ContentWrapper className="w-full">
      {contentTitle && <p className="pb-6 text-title3 font-bold">{contentTitle}</p>}
      <Carousel
        settings={settings}
        items={contents}
        renderItem={(content, prefetchImages) => {
          const { id, title, subTitle, description, photoUrl, link } = content;

          return (
            <div key={title} className="relative h-72 w-full">
              <ImageWithFallback
                alt={id.toString()}
                className="relative rounded-lg"
                src={photoUrl || '/fallback-img-3704x2304.png'}
                layout="fill"
                objectFit="cover"
                sizes="(max-width: 640px) 83vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 33vw"
                fallbackSrc="/fallback-img-3704x2304.png"
                url={link && (isAbsoluteUrl(link) ? link : getRelativeUrl(link))}
                target={isAbsoluteUrl(link) ? '_blank' : '_self'}
                loading={prefetchImages ? 'eager' : 'lazy'}
              >
                <>
                  <div className="dark-gradient-layer-mini-site-card absolute bottom-0 size-full rounded-xl" />
                  <div className="absolute bottom-0 p-6 text-white">
                    <p className="text-title3 font-bold">{title}</p>
                    <div className="flex flex-col gap-1">
                      <p className="font-bold">{subTitle}</p>
                      <Markdown className="text-small">{description || ''}</Markdown>
                    </div>
                  </div>
                </>
              </ImageWithFallback>
            </div>
          );
        }}
      />
    </ContentWrapper>
  );
};

export default MiniSiteSquareFeature;
